/**
 * Provider factory + React wiring.
 *
 * `getProvider()` is the only place that decides mock vs api, and it decides
 * once. Components read the provider from context via `useProvider()`.
 */
import { createContext, createElement, useContext, useMemo, type ReactNode } from 'react';
import { config, type DataMode } from '../config';
import { ApiProvider } from './apiProvider';
import { MockProvider } from './mockProvider';
import type { DataProvider } from './provider';

let singleton: DataProvider | null = null;

/** Builds (once) the provider for the configured mode. */
export function getProvider(mode: DataMode = config.dataMode): DataProvider {
  if (singleton && singleton.mode === mode) return singleton;
  singleton = mode === 'api' ? new ApiProvider() : new MockProvider();
  return singleton;
}

/** Test seam: forces a specific provider instance. */
export function setProvider(provider: DataProvider | null): void {
  singleton = provider;
}

const ProviderContext = createContext<DataProvider | null>(null);

export function DataProviderScope({
  children,
  provider,
}: {
  children: ReactNode;
  provider?: DataProvider;
}) {
  const value = useMemo(() => provider ?? getProvider(), [provider]);
  return createElement(ProviderContext.Provider, { value }, children);
}

export function useProvider(): DataProvider {
  const provider = useContext(ProviderContext);
  if (!provider) {
    throw new Error('useProvider must be used inside <DataProviderScope>.');
  }
  return provider;
}

export * from './provider';
export * from './session';
export { ApiProvider } from './apiProvider';
export { MockProvider } from './mockProvider';

export { resetMockState, subscribeToMockState, MOCK_STATE_KEY } from './mockStore';
